import { BookingEngine, Rates, roomData } from "../../index";
import Selectors from "./Selectors";
import waitForSelectors from "../../utilities/waitForSelectors";
import { handleIntegrationInjector } from "../../hotelIntegrationHandlers";
import axios from "axios";
import { guestbookSites, environment } from "../../../settings/IntegrationConstants";
import moment from "moment";
import {
  setNativeValue,
  getBrowserInfo,
  getCurrencySymbol,
  getDeviceInfo,
  parsePrice
} from "../../utilities/utils";

declare global {
  interface Window {
    gb_data: any;
  }
}

const gbData = window.gb_data;

const Guestline: BookingEngine = {
  name: gbData.bookingEngine,
  hotel: {
    name: gbData.hotel.name,
    isAutomated: gbData.hotel.isAutomated,
    rateCodeExclusions: gbData.hotel.rateCodeExclusions,
    packageRateExlusions: gbData.hotel.packageRateExlusions,
    whiteLabel: gbData.hotel.whiteLabel,
    dontAbTrack: true,
    memberCode: gbData.hotel.memberCode,
    memberCodeType: gbData.hotel.memberCodeType,
    showEnrollment: JSON.parse(
      sessionStorage.getItem("gbShowEnrollment") || "true"
    ),
    widgetIdentifier: gbData.widget.identifier,
    primaryColor: gbData.widget.primaryColor,
    secondaryColor: gbData.widget.secondaryColor,
    widgetAnchored: gbData.widget.widgetAnchored,
    widgetWidth: gbData.widget.widgetWidth,
    widgetPositionTop: gbData.widget.widgetPositionTop,
    widgetPositionRight: gbData.widget.widgetPositionRight,
    widgetPositionBottom: gbData.widget.widgetPositionBottom,
    widgetPositionLeft: gbData.widget.widgetPositionLeft,
    inheritBeFont: gbData.widget.inheritBeFont,
    desktopEnrollmentPrimaryColor: gbData.widget.desktopEnrollmentPrimaryColor,
    benefits: gbData.widget.benefits,
  },
  selectors: Selectors,
  singlePageApp: true,
  widgetFixedPosition: !gbData.widget.widgetAnchored,
  largeEnrollmentMessage: true,
  activeIntervals: [],
  saveInfoAtConfirmation: false,
  // grab widget target
  getWidgetTarget: () =>
    document.querySelector(Guestline.selectors.widget.target),
  // should user log in, grab the member code and input it (if member code exists)
  getMemberCode: () => {
    const params = new URL(window.location.href).searchParams;
    const groupPromoCode = params.get("promoCode");

    if (
      Guestline.hotel.memberCode &&
      Guestline.hotel.memberCode !== groupPromoCode
    ) {
      waitForSelectors(Guestline.selectors.ratesPage.waitFor, () => {
        let beBaseUrl = "";
        let beParams = "";
        if (window.location.href.split("?").length === 1) {
          beBaseUrl = window.location.href.split("?")[0];
        } else {
          beBaseUrl = window.location.href.split("?")[0];
          beParams = window.location.href.split("?")[1];
        }

        let urlParams = new URLSearchParams(beParams);

        urlParams.delete("promoCode");

        let paramsString = urlParams.toString();
        if (paramsString !== "") {
          paramsString += "&";
        }

        const memberRatesLink = `${beBaseUrl}?${paramsString}promoCode=${Guestline.hotel.memberCode}`;
        window.location.href = memberRatesLink;
      });
    }
  },
  clearMemberCode: () => {
    if (Guestline.hotel.memberCode) {
      let beBaseUrl = "";
      let beParams = "";
      if (window.location.href.split("?").length === 1) {
        beBaseUrl = window.location.href.split("?")[0];
      } else {
        beBaseUrl = window.location.href.split("?")[0];
        beParams = window.location.href.split("?")[1];
      }

      let urlParams = new URLSearchParams(beParams);

      urlParams.delete("promoCode");

      let paramsString = urlParams.toString();

      const memberRatesLink = `${beBaseUrl}?${paramsString}`;
      window.location.href = memberRatesLink;
    }
  },
  // grab rates info and target to inject, it also applies rate exclusions if any
  getRates: () => {
    const ratesInfo: Rates[] = [];
    const rates = document.querySelectorAll(
      Guestline.selectors.ratesPage.rates
    );
    rates.forEach((rate: any, index: number) => {
      const messageTarget = (rate as HTMLElement).parentElement?.parentElement
      // const messageTarget = (rate as HTMLElement).closest("ul")?.parentElement
      //   ?.parentElement?.parentElement;
      if (messageTarget) {
        const injectedElement = !!messageTarget.querySelector(
          Guestline.selectors.ratesPage.ratesMessage
        )
          ? messageTarget.querySelector(
              Guestline.selectors.ratesPage.ratesMessage
            )
          : document.createElement("div");
        if (
          !messageTarget.querySelector(
            Guestline.selectors.ratesPage.ratesMessage
          ) &&
          injectedElement
        ) {
          injectedElement.setAttribute(
            "class",
            Guestline.selectors.ratesPage.ratesMessage.substr(1)
          );
          messageTarget.append(injectedElement);
        }

        const taxedRate = window.gb_data.hotel.taxedRate;
        const ratePrice = parsePrice((rate as HTMLElement).innerHTML);
        let shouldDisplay = true;

        if (
          document.querySelector(Guestline.selectors.confirmationPage.waitFor)
        ) {
          shouldDisplay = false;
        }

        let calculatedRewards = (ratePrice / taxedRate) * 0.15;

        ratesInfo.push({
          rewardsAmount: (calculatedRewards as any).toFixed(0),
          shouldDisplay: shouldDisplay,
          target: injectedElement,
        });
      }
    });
    sessionStorage.setItem("rates", JSON.stringify(ratesInfo));
    return ratesInfo;
  },
  // grab confirmation number
  getConfirmationNumber: () => {
    let confimationNumbers = [] as string[];
    confimationNumbers.push(
      document.querySelector(
        Guestline.selectors.confirmationPage.confirmationNumber
      ).textContent
    );
    return confimationNumbers;
  },
  // grab confirmation message target
  getConfirmationTarget: () =>
    document.querySelector(
      Guestline.selectors.confirmationPage.confirmationMessageContainer
    ),
  // grab displayed rates page currency
  getRatesCurrency: () => {
    let currency = getCurrencySymbol("GBP");
    return currency;
  },
  // grab displayed checkout page currency
  getCheckoutCurrency: () => Guestline.getRatesCurrency(),
  // grab currency code to save
  getCurrencyToSave: () => Guestline.getRatesCurrency(),
  // grab language
  getLanguage: () => {
    return "en";
  },
  // grab enrollment target
  getEnrollmentTarget: () =>
    document.querySelector(
      Guestline.selectors.paymentPage.enrollmentMessageContainer
    ),
  // grab checkin date
  getPaymentPageCheckinDate: () => {
    let checkin = document.getElementsByName(
      Guestline.selectors.paymentPage.checkin
    ) as NodeListOf<HTMLInputElement>;
    return checkin[0].value;
  },
  // grab checkout date
  getPaymentPageCheckoutDate: () => {
    let checkout = document.getElementsByName(
      Guestline.selectors.paymentPage.checkout
    ) as NodeListOf<HTMLInputElement>;
    return checkout[0].value;
  },
  // prefill user info if user logged in
  prefillUserInfo: (firstName = "", lastName = "", email = "") => {
    waitForSelectors(Guestline.selectors.paymentPage.waitFor, () => {
      const emailInput = document.querySelector(
        Guestline.selectors.paymentPage.email
      );
      const firstNameInput = document.querySelector(
        Guestline.selectors.paymentPage.firstname
      );
      const lastNameInput = document.querySelector(
        Guestline.selectors.paymentPage.lastname
      );
      const event = new Event("input", { bubbles: true });

      setNativeValue(firstNameInput, firstName);
      firstNameInput.focus();
      firstNameInput.dispatchEvent(event);
      firstNameInput.blur();

      setNativeValue(lastNameInput, lastName);
      lastNameInput.focus();
      lastNameInput.dispatchEvent(event);
      lastNameInput.blur();

      setNativeValue(emailInput, email);
      emailInput.focus();
      emailInput.dispatchEvent(event);
      emailInput.blur();
    });
  },
  // grab email
  getGuestEmail: () =>
    document.querySelector(Guestline.selectors.paymentPage.email).value.trim(),
  // grab first name
  getGuestFirstName: () =>
    document
      .querySelector(Guestline.selectors.paymentPage.firstname)
      .value.trim(),
  // grab last name
  getGuestLastName: () =>
    document
      .querySelector(Guestline.selectors.paymentPage.lastname)
      .value.trim(),
  // grab rate type
  getRateType: () => {
    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.paymentPage.selectedRooms
    );
    let rateTypes: string[] = [];

    let rateType;
    for (var i = 0; i < selectedRooms.length; i++) {
      rateType = document.querySelector(
        'input[name="bookings[' + i + '][rateName]"]'
      ) as HTMLInputElement;

      rateTypes.push(rateType.value);
    }
    return rateTypes;
  },
  // grab roomName
  getRoomName: () => {
    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.paymentPage.selectedRooms
    );
    let roomNames: string[] = [];

    let roomName;
    for (var i = 0; i < selectedRooms.length; i++) {
      roomName = document.querySelector(
        'input[name="bookings[' + i + '][roomName]"]'
      ) as HTMLInputElement;

      roomNames.push(roomName.value);
    }
    return roomNames;
  },
  getNumberOfAdults: () => {
    let adults = document.getElementsByName(
      Guestline.selectors.paymentPage.adults
    ) as NodeListOf<HTMLInputElement>;
    return Number(adults[0].value);
  },
  getNumberOfDays: () => {
    return [1];
  },
  // grab rateCodes
  getRateTypes: () => {
    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.paymentPage.selectedRooms
    );
    let rateTypes: string[] = [];

    let rateType;
    for (var i = 0; i < selectedRooms.length; i++) {
      rateType = document.querySelector(
        'input[name="bookings[' + i + '][rateId]"]'
      ) as HTMLInputElement;

      rateTypes.push(rateType.value);
    }
    return rateTypes;
  },
  // grab promo code applied
  getPromoCode: () => {
    let promoCode = document.getElementsByName(
      Guestline.selectors.paymentPage.promoCode
    ) as NodeListOf<HTMLInputElement>;

    if (document.querySelector(Guestline.selectors.paymentPage.waitFor)) {
      return promoCode[0].value;
    } else {
      let promo = document.querySelector("#promoCode") as HTMLInputElement;
      if (promo != null) {
        return promo.value;
      } else {
        return "";
      }
    }
  },
  // grab group code applied
  getGroupCode: () => {
    return "";
  },
  // grab subtotal to save
  getSubtotal: () => {
    let subtotal = 0;
    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.ratesPage.selectedRooms
    );
    let roomPriceText;
    const taxedRate = window.gb_data.hotel.taxedRate;
    selectedRooms.forEach((room: any) => {
      roomPriceText = parsePrice(room
        .querySelector(Guestline.selectors.ratesPage.subtotal)
        .textContent.substring(1));
      subtotal += roomPriceText / taxedRate;
    });

    return Number(subtotal.toFixed(2));
  },
  // grab grand total to save
  getTotalWithTaxes: () => {
    const taxedRate = window.gb_data.hotel.taxedRate;
    let subtotal = Guestline.getSubtotal();
    return Number((Number(subtotal) * taxedRate).toFixed(2));
  },
  // aggregrate reservation data
  getReservationData: () => {
    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.ratesPage.selectedRooms
    );
    let rateTypes: string[] = [];
    let roomNames: string[] = [];
    let resData: roomData[] = [];

    selectedRooms.forEach((_: any, index: number) => {
      rateTypes.push(Guestline.getRateType()[index]);
      roomNames.push(Guestline.getRoomName()[index]);
    });

    resData.push({
      email: Guestline.getGuestEmail(),
      first_name: Guestline.getGuestFirstName(),
      last_name: Guestline.getGuestLastName(),
      confirmation_number: "",
      checkin_date: Guestline.getPaymentPageCheckinDate(),
      checkout_date: Guestline.getPaymentPageCheckoutDate(),
      rate_type: rateTypes.join(" + "),
      room_name: roomNames.join(" + "),
      currency_code: "GBP",
      lang_code: "en",
      total: Guestline.getSubtotal().toString(),
      total_with_taxes: Guestline.getTotalWithTaxes().toString(),
      hotel_identifier: window.gb_data.widget.identifier,
      agreed_enrollment_checked: sessionStorage.getItem(
        "gbEnrolledChecked"
      ) as string,
      ab_version_viewed: sessionStorage.getItem("gbShowEnrollment") as string,
      browser_info: getBrowserInfo(),
      is_mobile: getDeviceInfo(),
      promo_code: Guestline.getPromoCode ? Guestline.getPromoCode() : "",
      group_code: Guestline.getGroupCode ? Guestline.getGroupCode() : "",
    });

    return resData;
  },
  // grabs cash rewards
  getRewards: (percentage: number) => {
    let calculatedRewards = 0;
    let roomPriceText;
    const taxedRate = window.gb_data.hotel.taxedRate;

    let selectedRooms = document.querySelectorAll(
      Guestline.selectors.ratesPage.selectedRooms
    );

    let confirmationPageTotal = document.querySelectorAll(
      Guestline.selectors.confirmationPage.total
    );
    if (confirmationPageTotal.length > 0) {
      let subtotalText =
        confirmationPageTotal[0].textContent;
      let subtotal = parsePrice(subtotalText);
      calculatedRewards = (subtotal / taxedRate) * percentage;
    } else {
      selectedRooms.forEach((room: any) => {
        roomPriceText = room
          .querySelector(Guestline.selectors.paymentPage.subtotal)
          .textContent;
        var roomPrice = parsePrice(roomPriceText);
        calculatedRewards += (roomPrice / taxedRate) * percentage;
      });
    }

    return Number(calculatedRewards).toFixed(2);
  },
  // checks if messaging should be displayed
  isRateExcluded: (rate: string) => {
    const packageRateExlusions = Guestline.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return true;
      }
    }
    return false;
  },
  // gets the rate exclusion matching the rate code
  getRateExcluded: (rate: string) => {
    const packageRateExlusions = Guestline.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return packageRateExlusion;
      }
    }
  },
  // checks if messaging should be displayed
  shouldDisplayMessaging: () => {
    sessionStorage.setItem("rateExcluded", "false");
    //  if show enrollment (from AB is set to false)
    if (!JSON.parse(sessionStorage.getItem("gbShowEnrollment") || "true"))
      return false;
    const promoCode = Guestline.getPromoCode ? Guestline.getPromoCode() : "";
    const groupCode = Guestline.getGroupCode ? Guestline.getGroupCode() : "";
    const rateCodeExclusions = Guestline.hotel.rateCodeExclusions;
    for (const rateCodeExclusion of rateCodeExclusions as any) {
      // Do not show
      if (
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          promoCode.toLowerCase() ||
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          groupCode.toLowerCase()
      ) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }
    if (
      window.location.href.indexOf("confirmation") > -1 ||
      window.location.href.indexOf("bookingReference") > -1
    ) {
      return false;
    }
    // Show
    return true;
  },
  // checks if messaging should be partially displayed
  shouldDisplayPartialMessaging: () => {
    return true;
  },
  /** Uses waitUntilExists to check if the user should see widget
   * @param handler
   */
  addWidgetListener: (handler) => {
    const timeout = waitForSelectors(
      Guestline.selectors.widget.waitFor,
      handler
    );
    if (Guestline.activeIntervals) {
      Guestline.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on rates page then executes the passed handler function
   * @param handler
   */
  addRatesPageListener: (handler) => {
    const timeout = waitForSelectors(
      Guestline.selectors.ratesPage.waitFor,
      handler
    );
    if (Guestline.activeIntervals) {
      Guestline.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on payment page then executes the passed handler function
   * @param handler
   */
  addPaymentPageListener: (handler) => {
    const timeout = waitForSelectors(
      Guestline.selectors.paymentPage.waitFor,
      handler
    );
    if (Guestline.activeIntervals) {
      Guestline.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on confirmation page then executes the passed handler function
   * @param handler
   */
  addConfirmationPageListener: (handler) => {
    const timeout = waitForSelectors(
      Guestline.selectors.confirmationPage.waitFor,
      handler
    );
    if (Guestline.activeIntervals) {
      Guestline.activeIntervals.push(timeout);
    }
  },
  // listen for cancellation number
  addCancellationPageListener: () => {
    if (Guestline.addCancellationhandler) {
      const timeout = waitForSelectors(
        Guestline.selectors.confirmationPage.cancellationTarget,
        Guestline.addCancellationhandler
      );
      if (Guestline.activeIntervals) {
        Guestline.activeIntervals.push(timeout);
      }
    }
  },
  // submit cancellation
  addCancellationhandler: () => {
    const cancellationNumber = document.querySelector(
      Guestline.selectors.confirmationPage.cancellationNumber
    ).textContent;

    const today = moment().format("YYYY-MM-DD");

    if (cancellationNumber && cancellationNumber.length > 0) {
      const cancellationData = {
        confirmation_number: cancellationNumber,
        cancellation_number: cancellationNumber,
        cancellation_date: today,
        hotel_identifier: window.gb_data.widget.identifier,
      };
      axios
        .post(
          `${
            guestbookSites[environment as string].rewardsdash.url
          }/api/v1/submit_booking_engine_cancellation`,
          cancellationData,
          {
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          }
        )
        .then((response) => {
          if (response.data.success) {
            const element = document.querySelector(
              Guestline.selectors.confirmationPage.confirmationMessageContainer
            );
            if (!!element) {
              element.style.display = "none";
            }
          } else {
            console.log(response);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  },
  // create widget container
  createWidgetContainer: () => {
    document.querySelector(Guestline.selectors.widget.widget)?.remove();
    if (!document.querySelector(Guestline.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Guestline.selectors.widget.target
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Guestline.selectors.widget.widget.substr(1);
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  // create mobile widget container
  createWidgetMobileContainer: () => {
    document.querySelector(Guestline.selectors.widget.widget)?.remove();
    if (!document.querySelector(Guestline.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Guestline.selectors.widget.mobileWidgetTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Guestline.selectors.widget.widget.substr(1);
        injectedElement.setAttribute(
          "style",
          `
             z-index: 1000;
             left: 0px;
             width: 100%;
             bottom: 0px;
             position: fixed;
           `
        );
        messageTarget.append(injectedElement);
      }
    }
  },
  // create listener on scroll to anchor widget to sticky menu
  additionalWidgetFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
        #guestbook-sticky-widget .MuiButtonBase-root.MuiAccordionSummary-root,
        #guestbook-widget-desktop .MuiButtonBase-root.MuiAccordionSummary-root,
        #guestbook-widget .MuiButtonBase-root.MuiAccordionSummary-root {
            width: 100% !important;
            max-width: 100% !important;
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            margin: 0 !important;
            position: relative !important;
            transform: none !important;
            border: none !important;
            visibility: visible !important;
            top: 0 !important;
            opacity: 100% !important;
            text-transform: none !important;
            left: 0 !important;
        }
        </style>`
    );
    if (window.innerWidth <= 768) {
      document.addEventListener("scroll", (e) => {
        if (
          document.querySelector(
            Guestline.selectors.widget.mobileWidgetFixedTarget
          )
        ) {
          document.querySelector(Guestline.selectors.widget.widget)?.remove();
          if (
            !document.querySelector(Guestline.selectors.widget.stickyWidget)
          ) {
            const messageTarget = document.querySelector(
              Guestline.selectors.widget.mobileWidgetFixedTarget
            ).parentElement;
            if (messageTarget) {
              const injectedElement = document.createElement("div");
              injectedElement.id = Guestline.selectors.widget.stickyWidget.substr(
                1
              );
              injectedElement.setAttribute(
                "style",
                `
                margin-top: 8px;
                z-index: 1000;
                left: 0px;
                width: 100%;
              `
              );
              messageTarget.append(injectedElement);
            }
          }
        } else {
          document
            .querySelector(Guestline.selectors.widget.stickyWidget)
            ?.remove();
          if (!document.querySelector(Guestline.selectors.widget.widget)) {
            const messageTarget = document.querySelector(
              Guestline.selectors.widget.mobileWidgetTarget
            );
            if (messageTarget) {
              const injectedElement = document.createElement("div");
              injectedElement.id = Guestline.selectors.widget.widget.substr(1);
              messageTarget.append(injectedElement);
            }
          }
        }
      });
    }
  },
  // create payment message container
  createEnrollmentMessageContainer: () => {
    if (document.querySelector(Guestline.selectors.paymentPage.email)) {
      if (
        !document.querySelector(
          Guestline.selectors.paymentPage.enrollmentMessageContainer
        )
      ) {
        const messageTarget = document.querySelector(
          Guestline.selectors.paymentPage.enrollmentTarget
        ).parentElement?.parentElement?.parentElement?.parentElement;
        if (messageTarget) {
          const injectedElement = document.createElement("div");
          injectedElement.id = Guestline.selectors.paymentPage.enrollmentMessageContainer.substr(
            1
          );
          messageTarget.after(injectedElement);
        }
      }
    }
  },
  // create confirmation message container
  createConfirmationMessageContainer: () => {
    if (
      !document.querySelector(
        Guestline.selectors.confirmationPage.confirmationMessageContainer
      )
    ) {
      const messageTarget = document.querySelectorAll(
        Guestline.selectors.confirmationPage.messageTarget
      )[1];
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Guestline.selectors.confirmationPage.confirmationMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // set locale
  setLocale: () => {
    window.gb_data.I18n.locale = Guestline.getLanguage();
  },
  // grab subtotal to use to calculate rewards and display on enrollment/widget
  displaySubtotal: () => {
    let confirmationTotals = document.querySelectorAll(
      Guestline.selectors.confirmationPage.total
    );
    let subtotalValues;
    if (confirmationTotals.length > 0) {
      subtotalValues = confirmationTotals;
    } else {
      subtotalValues = document.querySelectorAll(
        Guestline.selectors.ratesPage.subtotal
      );
    }

    let subtotal = subtotalValues[subtotalValues.length - 2];
    if (subtotal) {
      return subtotal.textContent.replace(/[^\d.-]/g, "");
    } else {
      return 0;
    }
  },
  // save reservation data on the payment page
  savePaymentPageInfo: () => {
    const saveReservationData = (): void => {
      const resData = Guestline.getReservationData();
      sessionStorage.setItem("gbBookingDetails", JSON.stringify(resData));
    };

    let saveInfoTarget = document.querySelector(
      Guestline.selectors.paymentPage.saveInfoTarget
    );
    if (saveInfoTarget) {
      saveInfoTarget.addEventListener("click", () => saveReservationData());
    }
  },
  clearAllListeners: () => {
    if (!Guestline.activeIntervals) return;
    let length = Guestline.activeIntervals.length;
    for (let i = 0; i < length; i++) {
      let int = Guestline.activeIntervals.pop();
      clearInterval(int);
    }
  },
  injectIntegration: () => {
    handleIntegrationInjector(Guestline);
  },
  additionalRatesPageFunctions: () => {
    //  We need to change the overflow of the rates page so that the hoverover shows correctly
    // * EDIT: this was messing with the placement of the "view more rates" button
    // * Did not notice any issues after disabling this
    // let cards = document.querySelectorAll(
    //   ".MuiPaper-root.MuiCard-root.MuiPaper-elevation1.MuiPaper-rounded"
    // );

    // cards.forEach((card: any) => {
    //   card.parentElement.style.position = "revert";
    // });

    const resize = document.querySelector(Guestline.selectors.ratesPage.rates2);

    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
         .availability__rates .gb-rates-message{
            margin-left: -25px;
         }
          .gb-rates-message{
            font-size: 14px;
            display: flex;
            justify-content: right;
          }
          .gb-banner :first-child {
            top: 4px;
          }
          .MuiCard-root {
            overflow: visible;
          }
          .message-container {
            ${resize ? 'width: 100px;' : ''}
          }
        </style>`
    );

    let meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "strict-origin-when-cross-origin"
    document.head.appendChild(meta);
  },

  additionalPaymentsPageFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
         #guestbook-enrollment {
            width: 100%;
            padding: 10px;
         }
         #guestbook-enrollment > div {
            border-radius: 4px;
         }
       </style>`
    );

    if(document.querySelector("#guestbook-enrollment")){
      if(document.querySelectorAll("div[role='presentation']").length > 0){
        (document.querySelector("#guestbook-enrollment") as HTMLElement).style.display = 'none';
      } else {
        (document.querySelector("#guestbook-enrollment") as HTMLElement).style.display = 'block';
      }
    }

    let meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "strict-origin-when-cross-origin"
    document.head.appendChild(meta);
  },

  additionalConfirmationPageFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles-confirmation-page" type="text/css">
         #guestbook-widget-desktop, #guestbook-widget {
           display: none;
         }

         #guestbook-confirm {
           position: relative;
           padding-bottom: 50px;
           margin-top: -50px;
         }
       </style>`
    );
  },
};

export default Guestline;
